import { useEffect } from "react";

function upsertTag(tagName, selector, attributes = {}) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      element.removeAttribute(key);
      return;
    }
    element.setAttribute(key, value);
  });

  return element;
}

function removeManagedJsonLd() {
  document.head
    .querySelectorAll('script[data-blog-jsonld="true"]')
    .forEach((element) => element.remove());
}

export default function useSeoMetadata({
  title,
  description,
  canonicalUrl,
  robots,
  meta = [],
  openGraph = {},
  twitter = {},
  jsonLd = [],
}) {
  useEffect(() => {
    const previousTitle = document.title;

    document.title = title || previousTitle;

    upsertTag("meta", 'meta[name="description"]', {
      name: "description",
      content: description || "",
    });

    upsertTag("meta", 'meta[name="robots"]', {
      name: "robots",
      content: robots || "index,follow",
    });

    upsertTag("link", 'link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl || window.location.href,
    });

    const openGraphTags = [
      ["og:type", openGraph.type || "website"],
      ["og:title", openGraph.title || title || ""],
      ["og:description", openGraph.description || description || ""],
      ["og:image", openGraph.image || ""],
      ["og:url", openGraph.url || canonicalUrl || window.location.href],
      ["og:site_name", openGraph.siteName || ""],
      ["og:locale", openGraph.locale || "ru_RU"],
      ["article:published_time", openGraph.article?.publishedTime || ""],
      ["article:modified_time", openGraph.article?.modifiedTime || ""],
      ["article:author", openGraph.article?.author || ""],
      ["article:section", openGraph.article?.section || ""],
    ];

    openGraphTags.forEach(([property, content]) => {
      upsertTag("meta", `meta[property="${property}"]`, {
        property,
        content,
      });
    });

    const twitterTags = [
      ["twitter:card", twitter.card || "summary_large_image"],
      ["twitter:title", twitter.title || title || ""],
      ["twitter:description", twitter.description || description || ""],
      ["twitter:image", twitter.image || openGraph.image || ""],
    ];

    twitterTags.forEach(([name, content]) => {
      upsertTag("meta", `meta[name="${name}"]`, {
        name,
        content,
      });
    });

    meta.forEach((item) => {
      if (!item?.name) return;
      upsertTag("meta", `meta[name="${item.name}"]`, {
        name: item.name,
        content: item.content || "",
      });
    });

    removeManagedJsonLd();
    jsonLd
      .filter(Boolean)
      .forEach((entry) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.blogJsonld = "true";
        script.text = JSON.stringify(entry);
        document.head.appendChild(script);
      });

    return () => {
      document.title = previousTitle;
      removeManagedJsonLd();
    };
  }, [canonicalUrl, description, jsonLd, meta, openGraph, robots, title, twitter]);
}
